import React, { useState, useRef, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { X, Camera, Image as ImageIcon, Loader, Eye, Lock, Trash2, ClipboardCheck } from 'lucide-react';
import { uprightJpegBytes } from '../utils/imageOrient';
import { buildConditionReportPdfBytes } from '../utils/conditionReport';
import { bytesToBase64 } from '../utils/leaseContract';

// Vehicle Condition Report at handover — admin captures odometer, fuel, written
// condition AND photos PER AREA (exterior / interior / tyres / mechanical, plus a
// general pool), then generates a PDF filed against the vehicle's private documents
// (category "Condition Report") via the same admin-gated path as other vehicle docs.
const VEHICLE_ADMIN_URL = '/.netlify/functions/vehicle-admin-sb';

const labelStyle = { fontSize: '.7rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%', boxSizing: 'border-box' };
const FUEL = ['', 'Empty', '¼', '½', '¾', 'Full'];
const todayLabel = () => new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });

// Inspection areas — each gets its own notes + photo capture. `short` is used as the
// per-photo caption prefix in the PDF so every shot is tagged to where it was taken.
const AREAS = [
  { key: 'exterior',   short: 'Exterior',   label: 'Exterior — panels, paint, scratches, dents', ph: 'e.g. Dent on rear left door; scratches on rear bumper…' },
  { key: 'interior',   short: 'Interior',   label: 'Interior — seats, trim, dash, cleanliness',  ph: 'e.g. Tear on driver seat; worn cargo lining…' },
  { key: 'tyres',      short: 'Tyres',      label: 'Tyres & wheels',                              ph: 'e.g. Kerb scuff on front right rim; tread ok…' },
  { key: 'mechanical', short: 'Mechanical', label: 'Mechanical / dashboard warnings',            ph: 'e.g. No warning lights; A/C works…' },
];
const AREA_SHORT = Object.fromEntries(AREAS.map(a => [a.key, a.short]));
const base64ToBytes = (b64) => { const bin = atob(b64 || ''); const out = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i); return out; };

export default function ConditionReportDrawer({ vehicle, currentUser, existing, onClose, onSaved, addNotification }) {
  const { personnel } = useApp();
  const driver = useMemo(
    () => personnel.find(p => (p.loginName || '').toUpperCase() === (vehicle.login || '').toUpperCase()) || null,
    [personnel, vehicle.login],
  );
  const ex = existing?.report || null;          // editing an existing filed report
  const editing = !!existing?.docId;
  const photoId = useRef(0);

  const [odometer, setOdometer]   = useState(ex && ex.odometer != null && ex.odometer !== '' ? String(ex.odometer) : '');
  const [fuelLevel, setFuelLevel] = useState(ex?.fuelLevel || '');
  const [lesseeName, setLesseeName] = useState(ex?.lesseeName || driver?.name || '');
  const [inspectedBy, setInspectedBy] = useState(ex?.inspectedBy || currentUser?.name || currentUser?.login || '');
  const [notes, setNotes] = useState(ex?.notes || { exterior: '', interior: '', tyres: '', mechanical: '' });
  const [smokeOdour, setSmokeOdour] = useState(!!ex?.smokeOdour);
  const [extraNotes, setExtraNotes] = useState(ex?.extraNotes || '');
  const [photos, setPhotos] = useState(() => (ex?.photos || []).map(p => {
    const bytes = base64ToBytes(p.base64);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' }));
    return { id: ++photoId.current, url, bytes, caption: p.caption || '', area: p.area || 'general' };
  }));
  const [busy, setBusy] = useState('');

  const rego = (vehicle.rego || '').toUpperCase();
  const setNote = (k, v) => setNotes(prev => ({ ...prev, [k]: v }));

  const addPhotos = async (fileList, area) => {
    const files = Array.from(fileList || []);
    for (const f of files) {
      if (!f.type.startsWith('image/')) { addNotification('Only photos can be added', 'error'); continue; }
      try {
        const ab = await uprightJpegBytes(f, 1200, 0.6);
        const bytes = ab ? new Uint8Array(ab) : new Uint8Array(await f.arrayBuffer());
        const url = URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' }));
        setPhotos(prev => [...prev, { id: ++photoId.current, url, bytes, caption: '', area }]);
      } catch (e) { addNotification(`Could not add a photo: ${e.message}`, 'error'); }
    }
  };
  const removePhoto = (id) => setPhotos(prev => { const p = prev.find(x => x.id === id); if (p?.url) URL.revokeObjectURL(p.url); return prev.filter(x => x.id !== id); });
  const setCaption = (id, caption) => setPhotos(prev => prev.map(p => p.id === id ? { ...p, caption } : p));
  const countFor = (area) => photos.filter(p => p.area === area).length;

  const buildData = () => {
    const order = ['exterior', 'interior', 'tyres', 'mechanical', 'general'];
    const ordered = [...photos].sort((a, b) => order.indexOf(a.area) - order.indexOf(b.area));
    return {
      vehicle: { rego, year: vehicle.year, make: vehicle.make, model: vehicle.model },
      odometer, fuelLevel, lesseeName, inspectedBy, date: new Date(), notes: extraNotes, smokeOdour,
      areas: AREAS.map(a => ({ label: a.label, note: notes[a.key] })),
      photos: ordered.map(p => {
        const prefix = AREA_SHORT[p.area] || '';
        return { bytes: p.bytes, caption: [prefix, p.caption].filter(Boolean).join(' — ') };
      }),
    };
  };

  const handlePreview = async () => {
    const win = window.open('', '_blank');
    setBusy('preview');
    try {
      const bytes = await buildConditionReportPdfBytes(buildData());
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (win) win.location.href = url; else addNotification('Allow pop-ups to preview', 'info');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (win) win.close(); addNotification(`Preview failed: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const handleSave = async () => {
    if (!odometer) { addNotification('Enter the odometer reading at handover', 'error'); return; }
    setBusy('save');
    try {
      const bytes = await buildConditionReportPdfBytes(buildData());
      const fileBase64 = bytesToBase64(bytes);
      if (fileBase64.length > 5_700_000) { addNotification('Report too large — remove a few photos and try again', 'error'); setBusy(''); return; }
      const res = await fetch(VEHICLE_ADMIN_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: 'saveVehicleDocument',
          rego,
          category: 'Condition Report',
          label: `Condition Report — handover ${todayLabel()}`,
          filename: `Condition-Report-${rego}-${todayLabel().replace(/\s/g, '')}.pdf`,
          contentType: 'application/pdf',
          fileBase64,
          extracted: { conditionReport: true, odometer: Number(odometer) || 0, photoCount: photos.length, smokeOdour },
          uploadedBy: currentUser?.login || '',
        }),
      }).then(r => r.json());
      if (res.success) {
        const newDocId = res.id;
        // Persist the editable structured data (incl. photos) so this report can be re-opened & corrected.
        try {
          const report = {
            odometer, fuelLevel, lesseeName, inspectedBy, smokeOdour, extraNotes, notes,
            photos: photos.map(p => ({ base64: bytesToBase64(p.bytes), caption: p.caption, area: p.area })),
          };
          await fetch(VEHICLE_ADMIN_URL, { method: 'POST', body: JSON.stringify({ action: 'saveConditionReport', docId: newDocId, report }) });
        } catch { /* non-fatal — the PDF is filed regardless */ }
        // If we were editing, remove the previous version (PDF + its editable data).
        if (editing) {
          try { await fetch(VEHICLE_ADMIN_URL, { method: 'POST', body: JSON.stringify({ action: 'deleteVehicleDocument', id: existing.docId }) }); } catch {}
          try { await fetch(VEHICLE_ADMIN_URL, { method: 'POST', body: JSON.stringify({ action: 'deleteConditionReport', docId: existing.docId }) }); } catch {}
        }
        addNotification(`Condition report ${editing ? 'updated' : 'filed'} for ${rego}`, 'success');
        onSaved && onSaved(); onClose();
      } else addNotification(`Save failed: ${res.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const vehicleLabel = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Vehicle';
  const ta = { ...inputStyle, minHeight: 54, resize: 'vertical', fontSize: '0.85rem', padding: '8px 10px' };

  // Two small capture buttons (camera + library) that tag photos with `area`.
  const PhotoButtons = ({ area }) => (
    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
      <label className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
        <Camera size={13} /> Photo
        <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => { addPhotos(e.target.files, area); e.target.value = ''; }} />
      </label>
      <label className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
        <ImageIcon size={13} /> Library
        <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => { addPhotos(e.target.files, area); e.target.value = ''; }} />
      </label>
      {countFor(area) > 0 && <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', alignSelf: 'center' }}>{countFor(area)} photo{countFor(area) > 1 ? 's' : ''}</span>}
    </div>
  );

  // Thumbnails for one area, each with a caption + remove.
  const Thumbs = ({ area }) => {
    const list = photos.filter(p => p.area === area);
    if (!list.length) return null;
    return (
      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
        {list.map(p => (
          <div key={p.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, overflow: 'hidden', background: 'var(--hp-surface)' }}>
            <div style={{ position: 'relative' }}>
              <img src={p.url} alt="" style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
              <button className="btn btn-ghost btn-sm" onClick={() => removePhoto(p.id)} title="Remove" style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '2px 6px' }}><Trash2 size={12} /></button>
            </div>
            <input className="input" style={{ ...inputStyle, border: 'none', borderTop: '1px solid var(--hp-border)', fontSize: '0.72rem', borderRadius: 0 }} value={p.caption} onChange={e => setCaption(p.id, e.target.value)} placeholder="Caption (optional)" />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ width: 600, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>Fleet · Condition Report</div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.15rem' }}>{vehicleLabel} — <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>{rego}</span></h3>
            <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{editing ? 'Editing report — changes replace the filed PDF' : 'Records pre-existing condition at handover · photo each point'}</span>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Odometer at handover (km)</label>
            <input className="input" style={inputStyle} inputMode="numeric" value={odometer} onChange={e => setOdometer(e.target.value)} placeholder="e.g. 48210" />
          </div>
          <div>
            <label style={labelStyle}>Fuel level</label>
            <select className="input" style={inputStyle} value={fuelLevel} onChange={e => setFuelLevel(e.target.value)}>
              {FUEL.map(f => <option key={f} value={f}>{f || '—'}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Driver / Lessee</label>
            <input className="input" style={inputStyle} value={lesseeName} onChange={e => setLesseeName(e.target.value)} placeholder="Driver name" />
          </div>
          <div>
            <label style={labelStyle}>Inspected by</label>
            <input className="input" style={inputStyle} value={inspectedBy} onChange={e => setInspectedBy(e.target.value)} placeholder="Your name" />
          </div>
        </div>

        {/* Per-area: notes + photos */}
        {AREAS.map((a, i) => (
          <div key={a.key} style={{ marginTop: i ? 16 : 0, paddingTop: i ? 14 : 0, borderTop: i ? '1px solid var(--hp-border)' : 'none' }}>
            <label style={labelStyle}>{a.label}</label>
            <textarea className="input" style={ta} value={notes[a.key]} onChange={e => setNote(a.key, e.target.value)} placeholder={a.ph} />
            <PhotoButtons area={a.key} />
            <Thumbs area={a.key} />
          </div>
        ))}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, fontSize: '0.85rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={smokeOdour} onChange={e => setSmokeOdour(e.target.checked)} />
          Noticeable cigarette-smoke odour (pre-existing)
        </label>

        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--hp-border)' }}>
          <label style={labelStyle}>Additional notes & photos</label>
          <textarea className="input" style={ta} value={extraNotes} onChange={e => setExtraNotes(e.target.value)} placeholder="Anything else worth recording…" />
          <PhotoButtons area="general" />
          <Thumbs area="general" />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 18 }}>
          <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} disabled={!!busy} onClick={handlePreview}>
            {busy === 'preview' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Eye size={14} />} Preview PDF
          </button>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={!!busy} onClick={handleSave}>
            {busy === 'save' ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> {editing ? 'Updating…' : 'Filing…'}</> : <><Lock size={14} /> {editing ? 'Update report' : 'Generate & file report'}</>}
          </button>
        </div>
        <div style={{ marginTop: 14, fontSize: '0.72rem', color: 'var(--hp-muted)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <ClipboardCheck size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          Each photo is tagged to its area in the PDF. Filed privately under the vehicle’s documents — print or download it for both parties to sign at handover.
        </div>
      </div>
    </div>
  );
}
